import type { VendorProfileData } from '../models/vendor-profile.model';

/**
 * Modul aturan: menjawab SATU pertanyaan — **"apa yang wajib?"**
 *
 * Tidak pernah menyentuh data entri. Semua nilai eksplisit, tidak ada default tersembunyi.
 *
 * Lihat plans/KONSEP-PANEL-VALIDATION.md §5.
 */

/** Atribut vendor yang menentukan syarat kelengkapan. */
export interface Vendor {
  id: string;
  /** `'PT' | 'CV' | 'YAYASAN' | 'PERORANGAN' | …` — `''` bila BE mengirim null. */
  legalOrgTypeIdent: string;
  /** `'ID' | …` — `''` bila BE mengirim null. */
  countryCode: string;
  certificateIso: boolean;
}

/** Badan usaha sederhana — tidak wajib punya izin usaha sama sekali. */
const SIMPLE_ORG: readonly string[] = ['YAYASAN', 'PERORANGAN', 'LEMBAGA', 'FIRMA', 'KOPERASI'];

export const isSimpleOrg = (v: Vendor): boolean => SIMPLE_ORG.includes(v.legalOrgTypeIdent);
export const isPtCv = (v: Vendor): boolean =>
  v.legalOrgTypeIdent === 'PT' || v.legalOrgTypeIdent === 'CV';
export const isDomestic = (v: Vendor): boolean => v.countryCode === 'ID';

/** Jumlah minimum entri untuk panel bertipe "minimum count". */
export const MIN_ENTRY = 1;

/** Jenis dokumen pajak yang wajib ada. */
export const requiredTaxTypes = (_v: Vendor): readonly string[] => ['NPWP'];

/**
 * Jenis izin usaha yang wajib ada — bergantung bentuk usaha, negara, dan klaim sertifikat ISO.
 *
 * `[...base, 'ISO']` menghasilkan SALINAN. Jangan `push` ke array konstanta: itu akan mengubah
 * syarat untuk semua vendor berikutnya di sesi yang sama.
 */
export function requiredBusinessPermits(v: Vendor): readonly string[] {
  const base = isPtCv(v)
    ? ['TDP', 'SIUP', 'PI', 'SKDP']
    : isDomestic(v)
      ? isSimpleOrg(v)
        ? []
        : ['TDP', 'SIUP', 'PI']
      : ['CER', 'PI'];

  return v.certificateIso ? [...base, 'ISO'] : base;
}

/** Jenis keanggotaan pengurus yang wajib ada. */
export const requiredBoardTypes = (_v: Vendor): readonly string[] => ['BOD'];

/** Jenis akta yang wajib ada. */
export const requiredDeedTypes = (_v: Vendor): readonly string[] => ['AKTA_PENDIRIAN_PERUSAHAAN'];

/**
 * `GET api/vendor/vendors/current_user` → `Vendor`.
 *
 * Tidak ada request tambahan: shell sudah mengambil profil ini dan menyimpannya di
 * `VendorProfileStore` (PLAN-PROFILE-TAB §D2).
 *
 * Field yang `null` di BE jatuh ke `''`. Efeknya `isPtCv` & `isDomestic` false → cabang
 * `['CER','PI']`. Ini disengaja: aturan yang lebih ketat lebih baik daripada profil yang lolos
 * padahal bentuk badan usahanya tidak diketahui.
 */
export function toVendor(p: VendorProfileData): Vendor {
  return {
    id: p.id,
    legalOrgTypeIdent: p.legal_org?.legal_org_type?.ident_name ?? '',
    countryCode: p.country?.code ?? '',
    certificateIso: p.certificate_iso === true,
  };
}
